/**
=========================================================
* ClubOS - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @emotion/react for keyframes
import { keyframes } from "@emotion/react";

// @mui material components
import Grid from "@mui/material/Grid";
import LanguageIcon from "@mui/icons-material/Language";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// ClubOS examples
import PageLayout from "examples/LayoutContainers/PageLayout";

// Branding
import { ORGANIZATION, BACKGROUNDS } from "config/branding";

// Authentication layout components

// Animation keyframes
const backgroundFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const maskedPictureSlideIn = keyframes`
  0% {
    clipPath: polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%);
    opacity: 0;
  }
  100% {
    clipPath: polygon(55% 0%, 100% 0%, 85% 100%, 40% 100%);
    opacity: 1;
  }
`;

const detailsFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

function CoverLayout({ color, header, title, description, image, top, children }) {
  return (
    <PageLayout background="transparent">
      <SoftBox
        sx={{
          position: "relative",
          height: "100vh",
          width: "100vw",
          margin: 0,
          padding: 0,
          overflow: "hidden",
        }}
      >
        {/* Blurred Background Image */}
        <SoftBox
          sx={{
            position: "absolute",
            top: -10,
            left: -10,
            height: "calc(100vh + 20px)",
            width: "calc(100vw + 20px)",
            backgroundImage: `url(${BACKGROUNDS.menuImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: `blur(${BACKGROUNDS.menuImageBlur})`,
            zIndex: 0,
            animation: `${backgroundFadeIn} 1s ease-in-out`,
          }}
        />

        {/* Top Left - Club Logo & Name */}
        <SoftBox
          sx={{
            position: "absolute",
            top: 30,
            left: { xs: 0, sm: 80 },
            pl: { xs: "1rem", sm: 0 },
            zIndex: 10,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
            animation: `${detailsFadeIn} 1s ease-in-out 0.3s both`,
          }}
        >
          <img src="/images/Mohammedan_SC_logo.svg" alt="Club Logo" style={{ height: "70px", filter: "brightness(0) invert(1)" }} />
          <SoftTypography variant="h2" fontWeight="bold" color="white" sx={{ lineHeight: 1.1, fontFamily: "'Georgia', 'Playfair Display', serif" }}>
            {ORGANIZATION.clubNameLine1} {ORGANIZATION.clubNameLine2}
          </SoftTypography>
        </SoftBox>

        {/* White Border Frame */}
        <SoftBox
          sx={{
            display: { xs: "none", sm: "block" },
            position: "absolute",
            top: 0,
            right: 0,
            height: "100vh",
            width: "100vw",
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          <svg
            width="100%"
            height="100%"
            style={{ position: "absolute", top: 0, right: 0 }}
            preserveAspectRatio="none"
          >
            <polyline
              points="55%,0 100%,0 85%,100% 40%,100%"
              style={{
                fill: "none",
                stroke: "white",
                strokeWidth: "6",
              }}
            />
            <line x1="54.9%" y1="0" x2="39.9%" y2="100%" style={{ stroke: "white", strokeWidth: "0.125rem" }} />
            <line x1="100%" y1="0" x2="85%" y2="100%" style={{ stroke: "white", strokeWidth: "0.125rem" }} />
          </svg>
        </SoftBox>

        {/* White Border Parallelogram */}
        <SoftBox
          sx={{
            display: "none",
            position: "absolute",
            top: 0,
            right: 0,
            height: "100vh",
            width: "100vw",
            backgroundColor: "white",
            clipPath: "polygon(54.7% 0%, 100% 0%, 85.3% 100%, 40.3% 100%)",
            zIndex: 2,
          }}
        />

        {/* Background Image as Parallelogram */}
        <SoftBox
          sx={{
            display: { xs: "none", sm: "block" },
            position: "absolute",
            top: 0,
            right: 0,
            height: "100vh",
            width: "100vw",
            backgroundImage: `url(${image})`,
            backgroundSize: "contain",
            backgroundPosition: "right center",
            backgroundRepeat: "no-repeat",
            clipPath: "polygon(55% 0%, 100% 0%, 85% 100%, 40% 100%)",
            zIndex: 1,
            animation: `${maskedPictureSlideIn} 1s ease-in-out`,
          }}
        />

        {/* AthElite Branding at Top Right of Masked Picture */}
        <SoftBox
          sx={{
            display: { xs: "none", sm: "flex" },
            position: "absolute",
            top: 30,
            right:120,
            zIndex: 5,
            alignItems: "center",
            gap: 2,
            animation: `${detailsFadeIn} 1s ease-in-out 0.4s both`,
          }}
        >
          <img src="/images/Athelite-light.png" alt="AthElite Logo" style={{ height: "80px" }} />
          <SoftTypography
            variant="h3"
            fontWeight="bold"
            sx={{
              whiteSpace: "nowrap",
              fontFamily: "'Montserrat', 'Poppins', sans-serif",
              background: "linear-gradient(10deg, #f5f5dc 0%, #ffb700 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            AthElite Sports Management
          </SoftTypography>
        </SoftBox>

        {/* Content Overlay */}
        <SoftBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100vh",
            width: { xs: "100%", sm: "50%" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            animation: `${detailsFadeIn} 1s ease-in-out 0.5s both`,
          }}
        >
          <SoftBox sx={{ maxWidth: "400px", width: "100%", px: 3 }}>
            <SoftBox mb={3}>
              {!header ? (
                <>
                  <SoftBox mb={1} mt={10}>
                    <SoftTypography
                      variant="h2"
                      fontWeight="bold"
                      sx={{
                        background: "linear-gradient(10deg, #f5f5dc 0%, #ffb700 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {title}
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="body1" fontWeight="regular" color="white">
                    {description}
                  </SoftTypography>
                </>
              ) : (
                header
              )}
            </SoftBox>
            <SoftBox>{children}</SoftBox>
          </SoftBox>
        </SoftBox>

        {/* Bottom Right - Website Link */}
        <SoftBox
          sx={{
            display: { xs: "none", sm: "flex" },
            position: "absolute",
            bottom: 30,
            right: 80,
            marginRight: "150px",
            zIndex: 10,
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            animation: `${detailsFadeIn} 1s ease-in-out 0.6s both`,
          }}
        >
          <LanguageIcon sx={{ color: "#000000", fontSize: "1.5rem" }} />
          <SoftTypography
            component="a"
            href="https://www.mohammedansc.in"
            target="_blank"
            rel="noopener noreferrer"
            variant="h5"
            fontWeight="bold"
            sx={{
              textDecoration: "none",
              color: "#000000",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            www.mohammedansc.in
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </PageLayout>
  );
}

// Setting default values for the props of CoverLayout
CoverLayout.defaultProps = {
  header: "",
  title: "",
  description: "",
  color: "info",
  top: 20,
};

// Typechecking props for the CoverLayout
CoverLayout.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  header: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string.isRequired,
  top: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export default CoverLayout;
